import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  TenantSwitcher,
} from '@/components';
import { currentUser as queryCurrentUser, refreshToken } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import access from './access';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

// 递归过滤菜单
function filterMenuByPermission(
  menuData: any[],
  canAccess: (path: string) => boolean,
  accessObj: Record<string, any>,
): any[] {
  return menuData
    .filter((item) => {
      // 没有 path 的菜单项（如重定向）直接通过
      if (!item.path) return true;
      // 登录页、欢迎页等公共页面不做权限检查
      if (item.path === '/welcome') return true;
      // 检查路由级别的 access 属性（如 isSystemAdmin）
      if (item.access && !accessObj[item.access]) {
        return false;
      }
      // 检查权限
      return canAccess(item.path);
    })
    .map((item) => ({
      ...item,
      children: item.children
        ? filterMenuByPermission(item.children, canAccess, accessObj)
        : undefined,
    }))
    .filter((item) => {
      // 如果是父菜单且所有子菜单都被过滤掉了，也隐藏父菜单
      if (item.children && item.children.length === 0) return false;
      return true;
    });
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    // 最多重试 2 次（初始请求 + 刷新后重试）
    const maxRetries = 2;
    let lastError: any;

    for (let i = 0; i < maxRetries; i++) {
      try {
        const msg = await queryCurrentUser();
        // 映射后端字段到前端模板使用的字段
        if (msg.data) {
          return {
            ...msg.data,
            name: msg.data.username || msg.data.full_name,  // 映射 username 到 name
            userid: String(msg.data.id),  // 映射 id 到 userid
          };
        }
      } catch (error: any) {
        lastError = error;

        // 如果是 401 错误且是第一次尝试，等待 token 刷新后重试
        if (error.response?.status === 401 && i === 0) {
          const refresh_token = localStorage.getItem('refresh_token');
          if (!refresh_token) {
            history.push(loginPath);
            return undefined;
          }

          try {
            const response = await refreshToken(refresh_token);

            // 兼容两种响应格式：直接返回 Token 或包装在 data 中
            const tokenData = response;
            const accessToken = tokenData?.access_token;
            const refreshTokenValue = tokenData?.refresh_token;

            if (accessToken) {
              localStorage.setItem('access_token', accessToken);
              if (refreshTokenValue) {
                localStorage.setItem('refresh_token', refreshTokenValue);
              }
              // 等待一小段时间确保 token 已保存
              await new Promise(resolve => setTimeout(resolve, 100));
              // 继续循环，重试请求
              continue;
            }
          } catch (refreshError) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            history.push(loginPath);
            return undefined;
          }
        }
      }
    }

    // 如果所有重试都失败
    return undefined;
  };
  const currentUser = await fetchUserInfo();
  // 如果不是登录页面，执行
  // const { location } = history;
  // if (
  //   ![loginPath, '/user/register', '/user/register-result'].includes(
  //     location.pathname,
  //   )
  // ) {
  //   const currentUser = await fetchUserInfo();
  //   return {
  //     fetchUserInfo,
  //     currentUser,
  //     settings: defaultSettings as Partial<LayoutSettings>,
  //   };
  // }
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 获取权限检查函数
  const accessObj = access(initialState);
  const { canAccessRoute } = accessObj;

  return {
    // 菜单数据渲染：根据权限过滤
    menuDataRender: (menuData) => {
      return filterMenuByPermission(menuData, canAccessRoute, accessObj);
    },
    actionsRender: () => [
      <TenantSwitcher key="tenant-switcher" />,
      // <Question key="doc" />,
      // <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
        return;
      }

      // 路由权限检查：防止用户直接通过 URL 访问没有权限的页面
      const publicPaths = [loginPath, '/welcome', '/user/register', '/404', '/403', '/account'];
      const currentPath = location.pathname;

      // 公共页面不检查权限
      if (publicPaths.some(p => currentPath.startsWith(p))) {
        return;
      }

      // 已登录用户检查路由权限
      if (initialState?.currentUser && !canAccessRoute(currentPath)) {
        history.push('/403');
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
        <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          <LinkOutlined />
          <span>OpenAPI 文档</span>
        </Link>,
      ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 开发环境使用相对路径，让代理转发；生产环境使用完整 URL
  baseURL: '/api/v1',
  ...errorConfig,
};
